import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, ViewTransitionInfo } from '@angular/router';
import { NavigationDirectionService } from '../../core/platform/navigation-direction.service';

/**
 * `onViewTransitionCreated` hook for the router's View Transitions: a directional push/pop between
 * sections inside the app shell, like a native stack. Navigating deeper slides the page in from the
 * right; going back (popstate, edge swipe) slides it the other way. The keyframes live in
 * styles/_motion.scss and key off the `data-nav-direction` attribute set here for the duration of
 * the transition; the app shell names its <main> only while that attribute is present, so the
 * sidebar and tab bar stay put while the page content moves.
 *
 * The transition is skipped (the DOM update still applies, just without animation) when:
 *  - the user prefers reduced motion,
 *  - either side is outside the app shell (landing, auth, stream overlay never animated), or
 *  - both routes share a section key, so navigating within one page never slides.
 */
export function onRouteViewTransition({ transition, from, to }: ViewTransitionInfo): void {
  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !inShell(from) || !inShell(to) || sectionKey(from) === sectionKey(to)) {
    transition.skipTransition();
    return;
  }
  const root = document.documentElement;
  root.dataset['navDirection'] = inject(NavigationDirectionService).direction();
  activeTransition = transition;
  // A quick second navigation starts its transition before this one's `finished` settles; only the
  // latest transition may clear the attribute, or the new one loses its names mid-capture.
  void transition.finished.finally(() => {
    if (activeTransition === transition) {
      activeTransition = null;
      delete root.dataset['navDirection'];
    }
  });
}

let activeTransition: ViewTransition | null = null;

const inShell = (snapshot: ActivatedRouteSnapshot): boolean => snapshot.root.firstChild?.routeConfig?.path === 'app';

// The deepest route's `data.animation` key (set per top-level section in app.routes.ts), falling
// back to its URL for routes that declare none.
const sectionKey = (snapshot: ActivatedRouteSnapshot): string => {
  let leaf = snapshot.root;
  while (leaf.firstChild) {
    leaf = leaf.firstChild;
  }
  return (leaf.data['animation'] as string | undefined) ?? leaf.url.join('/');
};
